import { useEffect, useRef, useState } from 'react';
// @ts-expect-error
import WMTSLayer from '@arcgis/core/layers/WMTSLayer';
// @ts-expect-error
import View from '@arcgis/core/views/MapView';
// @ts-expect-error
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import { useDispatch, useSelector } from 'react-redux';

import {
  resolveCrackmapStreetLevelGate,
  selectCrackmapOverviewBand,
} from 'domain/surveyDataManager/crackmaps/crackmapCatalog';
import type { DefectOverlayLayerKind } from 'domain/surveyDataManager/defectOverlays/overlayKinds';
import { isDefectOverlayLayerKind } from 'domain/surveyDataManager/defectOverlays/overlayKinds';
import {
  formatDefectOverviewLoadingMessage,
  formatDefectOverlayLoadFailure,
  resolveDefectOverlayProfile,
} from 'domain/surveyDataManager/defectOverlays/overlayProfile';
import type {
  DefectOverviewGridBand,
  DefectOverviewGridConfig,
} from 'domain/surveyDataManager/defectOverlays/overviewGrid';
import { resolveDefectOverviewGrid } from 'domain/surveyDataManager/defectOverlays/resolveOverviewGrid';
import { toActiveDefectMapLayerId } from 'domain/surveyDataManager/map/activeLayerMapId';
import type { ActiveDefectLayerId } from 'state/surveyDataManager/activeLayersSlice';
import { selectActiveDefectLayers } from 'state/surveyDataManager';
import { layerMapRenderSettled } from 'state/surveyDataManager/uiSlice';
import type { AppDispatch } from 'state/store';

const ZOOM_DEBOUNCE_MS = 200;

export interface DefectOverviewGridMapLoadingState {
  readonly isFetching: boolean;
  readonly message: string | null;
  readonly hasError: boolean;
  readonly errorMessage: string | null;
}

interface WmtsSublayerLike {
  readonly id?: string;
  styleId?: string;
  tileMatrixSetId?: string;
  imageFormat?: string;
}

interface WmtsLayerLike {
  id: string;
  title: string;
  opacity: number;
  visible: boolean;
  loadStatus?: string;
  loadError?: { message?: string } | null;
  activeLayer?: WmtsSublayerLike | null;
  sublayers?: {
    find: (predicate: (sublayer: WmtsSublayerLike) => boolean) => WmtsSublayerLike | undefined;
  };
  load?: () => Promise<unknown>;
  when?: () => Promise<unknown>;
  destroy?: () => void;
}

interface TrackedOverviewLayer {
  layer: WmtsLayerLike;
  layerKind: DefectOverlayLayerKind;
  sourceLayerId: string | null;
  loadPromise: Promise<void> | null;
  loadFailed: boolean;
  initialLoadUiStarted: boolean;
  activeBandLayerId: string | null;
  pendingBand: DefectOverviewGridBand | null;
  pendingOpacity: number;
  pendingVisible: boolean;
}

interface LayerViewUpdatingLike {
  readonly updating: boolean;
  when?: () => Promise<unknown>;
  watch: (
    property: string,
    callback: (value: boolean) => void,
  ) => { remove: () => void };
}

interface OverviewLoadingControls {
  readonly begin: (message: string) => void;
  readonly end: () => void;
  readonly fail: (errorMessage: string) => void;
  readonly clearError: () => void;
  readonly reset: (options?: { readonly clearError?: boolean }) => void;
}

function resolveOverviewLoadingLabel(
  layerKind: DefectOverlayLayerKind | string | null | undefined,
  sourceLayerId?: string | null,
): string {
  return (
    resolveDefectOverlayProfile(layerKind, { sourceLayerId })
      ?.overviewLoadingLabel ?? 'overlay grid'
  );
}

function overviewLoadingMessage(
  layerKind: DefectOverlayLayerKind | string | null | undefined,
  sourceLayerId?: string | null,
): string {
  return formatDefectOverviewLoadingMessage(
    resolveOverviewLoadingLabel(layerKind, sourceLayerId),
  );
}

function getOverviewErrorMessage(
  error: unknown,
  layerKind?: DefectOverlayLayerKind | string | null,
  sourceLayerId?: string | null,
): string {
  const label = resolveOverviewLoadingLabel(layerKind, sourceLayerId);
  if (error instanceof Error && error.message.trim()) {
    return formatDefectOverlayLoadFailure(label, error.message);
  }
  return formatDefectOverlayLoadFailure(label);
}

function useOverviewFetchLoadingState(): {
  readonly loadingState: DefectOverviewGridMapLoadingState;
  readonly controls: OverviewLoadingControls;
} {
  const loadingCountRef = useRef(0);
  const [loadingState, setLoadingState] = useState<DefectOverviewGridMapLoadingState>({
    isFetching: false,
    message: null,
    hasError: false,
    errorMessage: null,
  });

  const controlsRef = useRef<OverviewLoadingControls>({
    begin: (message: string) => {
      loadingCountRef.current += 1;
      setLoadingState({
        isFetching: true,
        message,
        hasError: false,
        errorMessage: null,
      });
    },
    end: () => {
      loadingCountRef.current = Math.max(0, loadingCountRef.current - 1);
      if (loadingCountRef.current === 0) {
        setLoadingState((prev) => ({
          ...prev,
          isFetching: false,
          message: null,
        }));
      }
    },
    fail: (errorMessage: string) => {
      // Keep other in-flight overview loads counted so concurrent layers
      // continue to show map loading state
      loadingCountRef.current = Math.max(0, loadingCountRef.current - 1);
      setLoadingState((prev) => ({
        isFetching: loadingCountRef.current > 0,
        message: loadingCountRef.current > 0 ? prev.message : null,
        hasError: true,
        errorMessage,
      }));
    },
    clearError: () => {
      setLoadingState((prev) =>
        prev.hasError
          ? { ...prev, hasError: false, errorMessage: null }
          : prev,
      );
    },
    reset: (options) => {
      loadingCountRef.current = 0;
      setLoadingState((prev) => ({
        isFetching: false,
        message: null,
        hasError: options?.clearError ? false : prev.hasError,
        errorMessage: options?.clearError ? null : prev.errorMessage,
      }));
    },
  });

  return { loadingState, controls: controlsRef.current };
}

function overviewMapLayerId(layerId: ActiveDefectLayerId): string {
  return `${toActiveDefectMapLayerId(layerId)}__overview`;
}

function readMapZoom(mapView: View): number {
  return typeof mapView.zoom === 'number' ? mapView.zoom : Number.NaN;
}

function createOverviewWmtsLayer(
  mapLayerId: string,
  title: string,
  config: DefectOverviewGridConfig,
  initialBand: DefectOverviewGridBand,
): WmtsLayerLike {
  return new WMTSLayer({
    id: mapLayerId,
    title: `${title} (overview)`,
    url: config.serviceUrl,
    serviceMode: 'KVP',
    opacity: 0,
    visible: false,
    listMode: 'hide',
    ...(config.layerEffect ? { effect: config.layerEffect } : {}),
    activeLayer: {
      id: initialBand.layerId,
      tileMatrixSetId: config.tileMatrixSet,
      styleId: initialBand.style,
      imageFormat: config.format,
    },
  }) as WmtsLayerLike;
}

function disposeTrackedOverview(
  mapView: View | null,
  tracked: TrackedOverviewLayer,
  mapLayerId?: string,
): void {
  if (mapView?.map) {
    try {
      mapView.map.remove(tracked.layer);
    } catch {
      // ignore remove races during unmount
    }
    if (mapLayerId && typeof mapView.map.findLayerById === 'function') {
      const orphan = mapView.map.findLayerById(mapLayerId);
      if (orphan && orphan !== tracked.layer) {
        try {
          mapView.map.remove(orphan);
        } catch {
          // ignore
        }
        if (typeof (orphan as { destroy?: () => void }).destroy === 'function') {
          (orphan as { destroy: () => void }).destroy();
        }
      }
    }
  }
  if (typeof tracked.layer.destroy === 'function') {
    tracked.layer.destroy();
  }
}

function bringOverviewToFront(mapView: View, layer: WmtsLayerLike): void {
  const map = mapView.map;
  if (!map || typeof map.reorder !== 'function') {
    return;
  }
  try {
    map.reorder(layer, Math.max(0, map.layers.length - 1));
  } catch {
    // ignore reorder races
  }
}

function startOverviewLoad(
  tracked: TrackedOverviewLayer,
  mapLayerId: string,
): Promise<void> {
  if (tracked.loadFailed) {
    tracked.loadPromise = null;
    tracked.loadFailed = false;
    tracked.initialLoadUiStarted = false;
  }
  if (tracked.loadPromise) {
    return tracked.loadPromise;
  }
  const loadFn =
    typeof tracked.layer.load === 'function'
      ? () => tracked.layer.load?.()
      : typeof tracked.layer.when === 'function'
        ? () => tracked.layer.when?.()
        : () => Promise.resolve();
  tracked.loadPromise = Promise.resolve(loadFn())
    .then(() => {
      if (tracked.layer.loadStatus === 'failed') {
        const detail = tracked.layer.loadError?.message?.trim();
        throw new Error(
          detail
            ? `Crack grid WMTS layer failed to load: ${detail}`
            : 'Crack grid WMTS layer failed to load',
        );
      }
    })
    .catch((error: unknown) => {
      tracked.loadFailed = true;
      console.error('Failed to load crackmap overview WMTS layer', mapLayerId, error);
      throw error instanceof Error
        ? error
        : new Error(getOverviewErrorMessage(error));
    });
  return tracked.loadPromise;
}


function attachOverviewLoadUi(
  tracked: TrackedOverviewLayer,
  layerId: ActiveDefectLayerId,
  layerKind: DefectOverlayLayerKind,
  sourceLayerId: string | null,
  loadPromise: Promise<void>,
  trackedById: Map<ActiveDefectLayerId, TrackedOverviewLayer>,
  loadingControls: OverviewLoadingControls,
  onSettled?: (layerId: ActiveDefectLayerId) => void,
): void {
  if (tracked.initialLoadUiStarted) {
    return;
  }
  tracked.initialLoadUiStarted = true;
  loadingControls.begin(overviewLoadingMessage(layerKind, sourceLayerId));
  void loadPromise.then(
    () => {
      const stillTracked = trackedById.get(layerId) === tracked;
      if (stillTracked) {
        loadingControls.clearError();
        loadingControls.end();
      } else {
        loadingControls.end();
      }
      onSettled?.(layerId);
    },
    (error: unknown) => {
      const stillTracked = trackedById.get(layerId) === tracked;
      if (!stillTracked) {
        loadingControls.end();
        onSettled?.(layerId);
        return;
      }
      loadingControls.fail(
        getOverviewErrorMessage(error, layerKind, sourceLayerId),
      );
      onSettled?.(layerId);
    },
  );
}

function resolveBandSublayer(
  layer: WmtsLayerLike,
  config: DefectOverviewGridConfig,
  band: DefectOverviewGridBand,
): WmtsSublayerLike {
  const fromCapabilities = layer.sublayers?.find(
    (sublayer) => sublayer.id === band.layerId,
  );
  if (fromCapabilities) {
    fromCapabilities.styleId = band.style;
    fromCapabilities.tileMatrixSetId = config.tileMatrixSet;
    fromCapabilities.imageFormat = config.format;
    return fromCapabilities;
  }
  return {
    id: band.layerId,
    tileMatrixSetId: config.tileMatrixSet,
    styleId: band.style,
    imageFormat: config.format,
  };
}

function applyOverviewBand(
  mapView: View,
  tracked: TrackedOverviewLayer,
  config: DefectOverviewGridConfig,
  band: DefectOverviewGridBand | null,
  opacity: number,
  layerVisible: boolean,
): void {
  const nextBandId = layerVisible && band ? band.layerId : null;
  const bandChanged = tracked.activeBandLayerId !== nextBandId;
  tracked.activeBandLayerId = nextBandId;

  if (!nextBandId || !band) {
    tracked.layer.visible = false;
    tracked.layer.opacity = 0;
    return;
  }

  if (bandChanged) {
    tracked.layer.activeLayer = resolveBandSublayer(tracked.layer, config, band);
    bringOverviewToFront(mapView, tracked.layer);
  }

  tracked.layer.visible = true;
  tracked.layer.opacity = opacity;
}

function ensureOverviewLayer(
  mapView: View,
  layerId: ActiveDefectLayerId,
  displayName: string,
  layerKind: DefectOverlayLayerKind,
  sourceLayerId: string | null,
  config: DefectOverviewGridConfig,
  trackedById: Map<ActiveDefectLayerId, TrackedOverviewLayer>,
  loadingControls: OverviewLoadingControls,
  onSettled?: (layerId: ActiveDefectLayerId) => void,
): TrackedOverviewLayer | null {
  const map = mapView.map;
  if (!map) {
    return null;
  }

  const existing = trackedById.get(layerId);
  if (existing) {
    existing.layer.title = `${displayName} (overview)`;
    existing.layerKind = layerKind;
    existing.sourceLayerId = sourceLayerId;
    return existing;
  }

  const initialBand = config.bands[0];
  if (!initialBand) {
    return null;
  }

  const mapLayerId = overviewMapLayerId(layerId);
  const stale = map.findLayerById?.(mapLayerId);
  if (stale) {
    map.remove(stale);
    if (typeof stale.destroy === 'function') {
      stale.destroy();
    }
  }

  const layer = createOverviewWmtsLayer(
    mapLayerId,
    displayName,
    config,
    initialBand,
  );
  map.add(layer);

  const tracked: TrackedOverviewLayer = {
    layer,
    layerKind,
    sourceLayerId,
    loadPromise: null,
    loadFailed: false,
    initialLoadUiStarted: false,
    activeBandLayerId: null,
    pendingBand: null,
    pendingOpacity: 1,
    pendingVisible: false,
  };
  trackedById.set(layerId, tracked);

  const loadPromise = startOverviewLoad(tracked, mapLayerId);
  attachOverviewLoadUi(
    tracked,
    layerId,
    layerKind,
    sourceLayerId,
    loadPromise,
    trackedById,
    loadingControls,
    onSettled,
  );

  return tracked;
}

function syncOverviewLayer(
  mapView: View,
  layerId: ActiveDefectLayerId,
  displayName: string,
  layerKind: DefectOverlayLayerKind,
  sourceLayerId: string | null,
  opacity: number,
  visible: boolean,
  config: DefectOverviewGridConfig,
  band: DefectOverviewGridBand | null,
  trackedById: Map<ActiveDefectLayerId, TrackedOverviewLayer>,
  loadingControls: OverviewLoadingControls,
  onSettled?: (layerId: ActiveDefectLayerId) => void,
): void {
  if (!visible) {
    const tracked = trackedById.get(layerId);
    if (tracked) {
      tracked.pendingBand = null;
      tracked.pendingOpacity = opacity;
      tracked.pendingVisible = false;
      disposeTrackedOverview(mapView, tracked, overviewMapLayerId(layerId));
      trackedById.delete(layerId);
    }
    onSettled?.(layerId);
    return;
  }

  const tracked = ensureOverviewLayer(
    mapView,
    layerId,
    displayName,
    layerKind,
    sourceLayerId,
    config,
    trackedById,
    loadingControls,
    onSettled,
  );
  if (!tracked) {
    onSettled?.(layerId);
    return;
  }

  tracked.pendingBand = band;
  tracked.pendingOpacity = opacity;
  tracked.pendingVisible = true;

  const mapLayerId = overviewMapLayerId(layerId);
  const loadPromise = startOverviewLoad(tracked, mapLayerId);
  const loadUiAlreadyStarted = tracked.initialLoadUiStarted;

  // Retries after a dismissed failure need their own loading UI cycle.
  attachOverviewLoadUi(
    tracked,
    layerId,
    layerKind,
    sourceLayerId,
    loadPromise,
    trackedById,
    loadingControls,
    onSettled,
  );

  if (loadUiAlreadyStarted) {
    void loadPromise.then(
      () => {
        onSettled?.(layerId);
      },
      () => {
        onSettled?.(layerId);
      },
    );
  }

  void loadPromise.then(
    () => {
      const stillTracked = trackedById.get(layerId);
      if (stillTracked !== tracked) {
        return;
      }
      applyOverviewBand(
        mapView,
        tracked,
        config,
        tracked.pendingBand,
        tracked.pendingOpacity,
        tracked.pendingVisible,
      );
    },
    () => {
      if (trackedById.get(layerId) !== tracked) {
        return;
      }
      // Error already surfaced via fail(); keep overview hidden.
      tracked.layer.visible = false;
      tracked.layer.opacity = 0;
      tracked.activeBandLayerId = null;
    },
  );
}

/**
 * Shows PNG WMTS grid overview tiles for crackmap layers when zoomed out.
 *
 * Uses a single WMTSLayer per defect overlay (one GetCapabilities). Gated zoom
 * only changes activeLayer to the band sublayer — it does not spawn extra WMTS
 * layers. At street zoom the overview layer is hidden so vector detail can take over.
 */
export function useDefectOverviewGridOnMap(
  view: View | null,
): DefectOverviewGridMapLoadingState {
  const dispatch = useDispatch<AppDispatch>();
  const activeLayers = useSelector(selectActiveDefectLayers);
  const trackedRef = useRef<Map<ActiveDefectLayerId, TrackedOverviewLayer>>(new Map());
  const activeLayersRef = useRef(activeLayers);
  activeLayersRef.current = activeLayers;
  const { loadingState, controls: loadingControls } = useOverviewFetchLoadingState();
  const loadingControlsRef = useRef(loadingControls);
  loadingControlsRef.current = loadingControls;

  const settlePendingMapRenderRef = useRef(
    (layerId: ActiveDefectLayerId): void => {
      dispatch(layerMapRenderSettled(layerId));
    },
  );
  settlePendingMapRenderRef.current = (layerId: ActiveDefectLayerId): void => {
    dispatch(layerMapRenderSettled(layerId));
  };

  // Drop overview spinner/error when every overlay is hidden or removed.
  useEffect(() => {
    const hasVisibleOverlay = activeLayers.some(
      (layer) => isDefectOverlayLayerKind(layer.layerKind) && layer.visible,
    );
    if (!hasVisibleOverlay) {
      loadingControlsRef.current.reset({ clearError: true });
    }
  }, [activeLayers]);

  useEffect(() => {
    if (!view?.map) {
      return;
    }

    const tracked = trackedRef.current;
    const overlayLayers = activeLayers.filter((layer) =>
      isDefectOverlayLayerKind(layer.layerKind),
    );
    const activeIds = new Set(overlayLayers.map((layer) => layer.id));

    for (const [layerId, entry] of [...tracked.entries()]) {
      if (!activeIds.has(layerId)) {
        disposeTrackedOverview(view, entry, overviewMapLayerId(layerId));
        tracked.delete(layerId);
      }
    }

    const zoom = readMapZoom(view);
    const atStreetLevel = resolveCrackmapStreetLevelGate(zoom);
    if (atStreetLevel) {
      // Overview grids are hidden at street zoom — never leave their spinner up.
      loadingControlsRef.current.reset({ clearError: true });
    }

    for (const layerMeta of overlayLayers) {
      if (!isDefectOverlayLayerKind(layerMeta.layerKind)) {
        continue;
      }
      const config = resolveDefectOverviewGrid(layerMeta.sourceLayerId);
      if (!config) {
        continue;
      }
      const band = atStreetLevel ? null : selectCrackmapOverviewBand(config, zoom);
      syncOverviewLayer(
        view,
        layerMeta.id,
        layerMeta.displayName,
        layerMeta.layerKind,
        layerMeta.sourceLayerId,
        layerMeta.opacity,
        layerMeta.visible,
        config,
        band,
        tracked,
        loadingControlsRef.current,
        (id) => settlePendingMapRenderRef.current(id),
      );
    }
  }, [view, activeLayers]);

  useEffect(() => {
    if (!view?.map) {
      return;
    }

    let debounceTimer: number | null = null;

    const refreshOverviews = (): void => {
      const zoom = readMapZoom(view);
      const tracked = trackedRef.current;
      const atStreetLevel = resolveCrackmapStreetLevelGate(zoom);

      if (atStreetLevel) {
        loadingControlsRef.current.reset({ clearError: true });
      }

      for (const layerMeta of activeLayersRef.current) {
        if (!isDefectOverlayLayerKind(layerMeta.layerKind)) {
          continue;
        }
        const config = resolveDefectOverviewGrid(layerMeta.sourceLayerId);
        if (!config) {
          continue;
        }
        const band = atStreetLevel ? null : selectCrackmapOverviewBand(config, zoom);
        const existing = tracked.get(layerMeta.id);
        const nextBandId =
          layerMeta.visible && band ? band.layerId : null;

        if (
          existing &&
          !existing.loadFailed &&
          existing.activeBandLayerId === nextBandId &&
          existing.layer.opacity === (nextBandId ? layerMeta.opacity : 0) &&
          existing.layer.visible === Boolean(nextBandId)
        ) {
          continue;
        }

        syncOverviewLayer(
          view,
          layerMeta.id,
          layerMeta.displayName,
          layerMeta.layerKind,
          layerMeta.sourceLayerId,
          layerMeta.opacity,
          layerMeta.visible,
          config,
          band,
          tracked,
          loadingControlsRef.current,
          (id) => settlePendingMapRenderRef.current(id),
        );
      }
    };

    const scheduleRefresh = (): void => {
      if (debounceTimer !== null) {
        window.clearTimeout(debounceTimer);
      }
      debounceTimer = window.setTimeout(() => {
        debounceTimer = null;
        refreshOverviews();
      }, ZOOM_DEBOUNCE_MS);
    };

    const zoomHandle = reactiveUtils.watch(() => view.zoom, scheduleRefresh);
    const stationaryHandle = reactiveUtils.watch(
      () => view.stationary as boolean,
      (isStationary: boolean) => {
        if (!isStationary) {
          return;
        }
        scheduleRefresh();
      },
    );

    return () => {
      if (debounceTimer !== null) {
        window.clearTimeout(debounceTimer);
      }
      zoomHandle.remove();
      stationaryHandle.remove();
    };
  }, [view]);

  // Pan tile fetches for the active overview layer only.
  useEffect(() => {
    if (!view?.map) {
      return;
    }

    let cancelled = false;
    const handles: Array<{ remove: () => void }> = [];
    let attachedUpdating = false;
    let lastWatchedBandId: string | null = null;
    let debounceTimer: number | null = null;

    const detachTileWatchers = (): void => {
      for (const handle of handles) {
        handle.remove();
      }
      handles.length = 0;
      if (attachedUpdating) {
        attachedUpdating = false;
        loadingControlsRef.current.end();
      }
    };

    const setTileUpdating = (
      updating: boolean,
      layerKind?: DefectOverlayLayerKind,
      sourceLayerId?: string | null,
    ): void => {
      if (cancelled) {
        return;
      }
      // Overview is hidden at street zoom — ignore stale layerView updates.
      if (resolveCrackmapStreetLevelGate(readMapZoom(view))) {
        if (attachedUpdating) {
          attachedUpdating = false;
          loadingControlsRef.current.end();
        }
        return;
      }
      if (updating && !attachedUpdating) {
        attachedUpdating = true;
        loadingControlsRef.current.begin(
          overviewLoadingMessage(layerKind, sourceLayerId),
        );
      } else if (!updating && attachedUpdating) {
        attachedUpdating = false;
        loadingControlsRef.current.end();
      }
    };

    const attachActiveLayerViews = (): void => {
      detachTileWatchers();
      lastWatchedBandId = null;

      if (resolveCrackmapStreetLevelGate(readMapZoom(view))) {
        loadingControlsRef.current.reset({ clearError: true });
        return;
      }

      for (const tracked of trackedRef.current.values()) {
        const activeBandId = tracked.activeBandLayerId;
        if (!activeBandId || !tracked.layer.visible || tracked.loadFailed) {
          continue;
        }

        const isBandSwitch = lastWatchedBandId !== activeBandId;
        lastWatchedBandId = activeBandId;
        const layerKind = tracked.layerKind;
        const sourceLayerId = tracked.sourceLayerId;

        void view
          .whenLayerView(tracked.layer)
          .then((layerView: LayerViewUpdatingLike) => {
            if (cancelled) {
              return;
            }
            if (
              !tracked.layer.visible ||
              tracked.activeBandLayerId !== activeBandId ||
              resolveCrackmapStreetLevelGate(readMapZoom(view))
            ) {
              return;
            }
            // First paint after a gated sublayer swap: skip loader.
            if (isBandSwitch) {
              return;
            }
            if (layerView.updating) {
              setTileUpdating(true, layerKind, sourceLayerId);
            }
            const handle = reactiveUtils.watch(
              () => layerView.updating as boolean,
              (updating: boolean) => {
                setTileUpdating(updating, layerKind, sourceLayerId);
              },
            );
            handles.push(handle);
          })
          .catch((error: unknown) => {
            if (cancelled || tracked.loadFailed) {
              return;
            }
            // Layer was removed/toggled off while whenLayerView was in flight
            if (![...trackedRef.current.values()].includes(tracked)) {
              return;
            }
            console.error('Failed to view overview WMTS layer', error);
            tracked.loadFailed = true;
            loadingControlsRef.current.fail(
              getOverviewErrorMessage(error, layerKind, sourceLayerId),
            );
          });
      }
    };

    const scheduleAttach = (): void => {
      if (debounceTimer !== null) {
        window.clearTimeout(debounceTimer);
      }
      debounceTimer = window.setTimeout(() => {
        debounceTimer = null;
        attachActiveLayerViews();
      }, ZOOM_DEBOUNCE_MS);
    };

    attachActiveLayerViews();
    const zoomHandle = reactiveUtils.watch(() => view.zoom, scheduleAttach);
    const stationaryHandle = reactiveUtils.watch(
      () => view.stationary as boolean,
      (isStationary: boolean) => {
        if (!isStationary) {
          return;
        }
        attachActiveLayerViews();
      },
    );

    return () => {
      cancelled = true;
      if (debounceTimer !== null) {
        window.clearTimeout(debounceTimer);
      }
      zoomHandle.remove();
      stationaryHandle.remove();
      // Only detach pan/tile watchers, do not reset the shared loading count
      // Concurrent overview loads (and clicked button) must survive when
      // another layer is added/removed from activeLayers
      detachTileWatchers();
    };
  }, [view, activeLayers]);

  useEffect(() => {
    const mapView = view;
    return () => {
      const tracked = trackedRef.current;
      for (const [layerId, entry] of tracked.entries()) {
        disposeTrackedOverview(mapView, entry, overviewMapLayerId(layerId));
      }
      tracked.clear();
      loadingControlsRef.current.reset({ clearError: true });
    };
  }, [view]);

  return loadingState;
}
